import {Component, computed, effect, input, output, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs';
import {
  MatTree, MatTreeNode,
  MatTreeNodeDef, MatTreeNodePadding,
  MatTreeNodeToggle
} from '@angular/material/tree';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule} from '@angular/material/icon';
import {MatCheckbox} from '@angular/material/checkbox';
import {ExtendedFacetNode, FacetNode, FacetNodeType, SimpleFacetNode} from '../../domain/search.model';

type ExtendedTreeNode = Omit<ExtendedFacetNode, 'children'> & {
  completed?: boolean;
  children?: ExtendedTreeNode[];
};

type SimpleTreeNode = SimpleFacetNode & {
  completed?: boolean;
};

type TreeNode = ExtendedTreeNode | SimpleTreeNode;

@Component({
  selector: 'app-multi-tree-select',
  imports: [
    MatExpansionModule,
    MatTree,
    MatTreeNodeDef,
    MatIconModule,
    MatTreeNodeToggle,
    MatTreeNodePadding,
    MatTreeNode,
    MatCheckbox,
  ],
  templateUrl: './multi-tree-select.html',
  styleUrl: './multi-tree-select.scss',
})
export class MultiTreeSelect {
  public readonly label = input.required<string>();
  public readonly nodes = input.required<FacetNode[]>();
  public readonly resetVersion = input(0);

  private readonly _completedNodeIds = signal<Set<string>>(new Set());
  private readonly _expandedNodeIds = signal<Set<string>>(new Set());

  public readonly selectionChange = output<FacetNode[]>();

  protected readonly dataSource = computed(() =>
    this.nodes().map(n => this._toTreeNode(n))
  );

  private readonly _matTree = viewChild.required(MatTree<TreeNode>);

  protected childrenAccessor = (node: TreeNode): TreeNode[] =>
    node.type === FacetNodeType.EXTENDED ? node.children ?? [] : [];

  protected hasChild = (_: number, node: TreeNode): boolean =>
    node.type === FacetNodeType.EXTENDED && !!node.children?.length;

  protected nodeLabel(node: TreeNode): string {
    return node.type === FacetNodeType.EXTENDED ? node.label : node.value;
  }

  private _nodeKey(node: TreeNode): string {
    return node.type === FacetNodeType.EXTENDED ? node.id : node.value;
  }

  constructor() {
    effect(() => {
      this.resetVersion();
      this._completedNodeIds.set(new Set());
      this._expandedNodeIds.set(new Set());
    });

    toObservable(this._matTree).pipe(
      switchMap(tree => tree._getExpansionModel().changed),
      takeUntilDestroyed()
    ).subscribe(change => {
      this._expandedNodeIds.update(ids => {
        const next = new Set(ids);
        change.added.forEach((node: TreeNode) => next.add(this._nodeKey(node)));
        change.removed.forEach((node: TreeNode) => next.delete(this._nodeKey(node)));
        return next;
      });
    });

    effect(() => {
      const data = this.dataSource();
      if (data.length === 0) return;

      queueMicrotask(() => {
        const tree = this._matTree();
        const expandedIds = this._expandedNodeIds();

        this._flattenNodes(data).forEach(node => {
          if (expandedIds.has(this._nodeKey(node))) {
            tree.expand(node);
          }
        });
      });
    });
  }

  protected partiallyComplete(node: TreeNode): boolean {
    return !node.completed &&
      node.type === FacetNodeType.EXTENDED &&
      !!node.children?.some(n => n.completed || this.partiallyComplete(n));
  }

  protected updateNode(checked: boolean, node: TreeNode) {
    node.completed = checked;
    const key = this._nodeKey(node);
    this._completedNodeIds.update(current => {
      const next = checked
        ? [...current, key]
        : [...current].filter(id => id !== key);
      return new Set(next);
    });
    this.selectionChange.emit(this._collectSelected(this.dataSource()));
  }

  private _flattenNodes(nodes: TreeNode[]): TreeNode[] {
    return nodes.flatMap(n => [n, ...this._flattenNodes(this.childrenAccessor(n))]);
  }

  private _toTreeNode(node: FacetNode): TreeNode {
    if (node.type === FacetNodeType.EXTENDED) {
      return {
        ...node,
        completed: this._completedNodeIds().has(node.id),
        children: node.children?.map(n => this._toTreeNode(n) as ExtendedTreeNode),
      };
    }
    return {
      ...node,
      completed: this._completedNodeIds().has(node.value),
    };
  }

  private _collectSelected(nodes: TreeNode[]): FacetNode[] {
    return nodes.flatMap(node => {
      const selected: FacetNode[] = node.completed ? [node as FacetNode] : [];
      const children = node.type === FacetNodeType.EXTENDED ? node.children ?? [] : [];
      return [...selected, ...this._collectSelected(children)];
    });
  }
}
